<template>
  <div id="business-payments-page" class="vs-con-loading__container">
    <vx-card class="mb-base" :title="$t('payments.filter')">
      <div class="vx-row">
        <!--Type -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">{{ $t('payments.type') }}</label>
          <v-select
            v-model="type"
            :clearable="false"
            :options="[
              { label: $t('payments.types.paid'), value: 'Paid' },
              { label: $t('payments.types.unpaid'), value: 'UnPaid' },
              { label: $t('payments.types.all'), value: 'Paid|UnPaid' }
            ]"
            :reduce="o => o.value"
            name="type"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
        </div>
        <!--Employee -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">{{ $t('employees.employees') }}</label>
          <v-select
            v-model="employees"
            :clearable="false"
            :options="employeeOptions"
            multiple
            :reduce="o => o.value"
            name="employee"
            :placeholder="$t('payments.types.all')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
        </div>
        <!--Start -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">{{ $t('payments.start_date') }}</label>

          <date-picker type="date" v-model="startDate" element="startDate" :max="$moment().format()" />
          <vs-input
            name="startDate"
            id="startDate"
            :value="
              startDate
                ? $moment(startDate)
                    .locale($vs.rtl ? 'fa' : 'en')
                    .format('dddd, DD MMMM YYYY')
                : ''
            "
            v-validate="'required'"
            class="w-full"
          />
        </div>
        <!--End -->
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="vs-input--label">{{ $t('payments.end_date') }}</label>

          <date-picker
            type="date"
            v-model="endDate"
            element="endDate"
            :min="startDate"
            :max="$moment().format()"
          />
          <vs-input
            name="endDate"
            id="endDate"
            :value="
              endDate
                ? $moment(endDate)
                    .locale($vs.rtl ? 'fa' : 'en')
                    .format('dddd, DD MMMM YYYY')
                : ''
            "
            class="w-full"
          />
        </div>
      </div>
    </vx-card>
    <vx-card>
      <div class="vx-row">
        <!--Time frame -->
        <div class="vx-col w-full md:w-1/3 mb-4">
          <label class="vs-input--label">{{ $t('payments.timeframe') }}</label>
          <v-select
            v-model="timeframe"
            :clearable="false"
            :options="[
              { label: $t('payments.timeframes.year'), value: 0 },
              { label: $t('payments.timeframes.month'), value: 1 },
              { label: $t('payments.timeframes.week'), value: 2 },
              { label: $t('payments.timeframes.day'), value: 3 },
              { label: $t('payments.timeframes.hour'), value: 4 }
            ]"
            :reduce="o => o.value"
            name="timeframe"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
        </div>
        <div class="vx-col w-full mt-4 pl-4">
          <p class="font-semibold">
            {{ $t('payments.total', { count: this.metadata.length }) | persian_digits }}: {{ total | toman }}
          </p>
        </div>
        <vue-frappe
          id="chart"
          class="vx-col w-full"
          :labels="chartOptions.labels"
          dir="ltr"
          type="axis-mixed"
          :height="500"
          :colors="['purple']"
          :dataSets="chartOptions.data"
          :yMarkers="[{ label: '', value: 0 }]"
          :tooltipOptions="{ formatTooltipY: d => d + ' تومان' }"
        />
      </div>
    </vx-card>
  </div>
</template>

<script>
import moduleMetadata from '@/store/metadata/moduleMetadata'
import moduleBusiness from '@/store/business/moduleBusiness'
import { VueFrappe } from 'vue2-frappe'

export default {
  name: 'Payments',
  components: {
    VueFrappe
  },
  data() {
    return {
      metadata: [],
      business: { employees: [], user: {} },
      type: 'Paid|UnPaid',
      timeframe: 3,
      employees: [],
      startDate: this.$moment()
        .startOf('jYear')
        .format(),
      endDate: this.$moment()
        .endOf('day')
        .format()
    }
  },
  computed: {
    filterQuery() {
      return {
        key: {
          $regex: `^(${this.type})[(]${this.business.user.id}:(${
            this.employees.length ? this.employees.join('|') : '.*'
          }).*[)]`
        },
        month: { $exists: this.timeframe > 0 },
        week: { $exists: this.timeframe > 1 },
        day: { $exists: this.timeframe > 2 },
        hour: { $exists: this.timeframe > 3 },
        created_at: {
          $gte: this.startDate,
          $lte: this.endDate
        },
        dateType: 'solar'
      }
    },
    total() {
      return Math.round(
        this.metadata.length ? this.metadata.reduce((accum, item) => accum + item.value, 0) : 0
      )
    },
    chartOptions() {
      let labels = []
      const valueList = this.metadata.reduce((p, c) => {
        let x = p.find(
          e =>
            e.year === c.year &&
            (this.timeframe > 0 ? e.month === c.month : true) &&
            (this.timeframe > 1 ? e.week === c.week : true) &&
            (this.timeframe > 2 ? e.day === c.day : true) &&
            (this.timeframe > 3 ? e.hour === c.hour : true)
        )
        if (!x) {
          p.push(Object.assign({ count: 1 }, c))
          labels.push(this.timeframeDate(c.created_at))
        } else {
          x.value += c.value
          x.count += 1
        }
        return p
      }, [])

      return {
        labels,
        data: [
          {
            chartType: 'bar',
            values: valueList.map(m => m.value)
          }
        ]
      }
    },
    employeeOptions() {
      const employees = []
      this.business.employees.forEach(e => {
        employees.push({ label: `${e.user.details.firstName} ${e.user.details.lastName}`, value: e.user.id })
      })
      return employees
    }
  },
  watch: {
    filterQuery() {
      this.fetchMetadata()
    }
  },
  methods: {
    fetchMetadata() {
      this.$vs.loading({
        container: '#business-payments-page'
      })
      this.$store
        .dispatch('metadata/fetchManyMetadata', { query: this.filterQuery })
        .then(res => {
          this.metadata = res.data
        })
        .finally(() => this.$vs.loading.close('#business-payments-page > .con-vs-loading'))
    },
    timeframeDate(date) {
      let result = ''
      let format = `${this.timeframe > 3 ? 'ساعت HH' : ''} YYYY${this.timeframe > 0 ? '/MM' : ''}${
        this.timeframe > 2 ? '/DD' : ''
      }`

      if (this.timeframe === 2) {
        const wd = this.$moment(date)
          .locale('fa')
          .weekday()
        const start = this.$moment(date)
          .locale('fa')
          .subtract(wd, 'day')
        const end = this.$moment(date)
          .locale('fa')
          .add(6 - wd, 'day')
        let endFormat = 'DD'

        if (!start.isSame(end, 'month')) endFormat = 'MM/DD'
        if (!start.isSame(end, 'year')) endFormat = 'YYYY/MM/DD'

        result = `${start.format('YYYY/MM/DD')}-${end.format(endFormat)}`
      } else
        result = this.$moment(date)
          .locale('fa')
          .format(format)
      return this.$filters.persian_digits(result)
    }
  },
  created() {
    this.$vs.loading()
    if (!moduleMetadata.isRegistered) {
      this.$store.registerModule('metadata', moduleMetadata)
      moduleMetadata.isRegistered = true
    }
    if (!moduleBusiness.isRegistered) {
      this.$store.registerModule('business', moduleBusiness)
      moduleBusiness.isRegistered = true
    }
    this.$store.commit('business/SET_BUSINESSES_QUERY', {
      userId: this.$store.state.AppActiveUser.id
    })
    this.$store
      .dispatch('business/fetchBusinesses')
      .then(res => {
        this.business = res.data.businesses[0]
        this.fetchMetadata()
      })
      .finally(() => this.$vs.loading.close())
  }
}
</script>
